import type { ErrorComponentProps, NotFoundRouteProps } from '@tanstack/react-router';
import React from 'react';
import { twMerge } from 'tailwind-merge';

import { Button, Stack } from 'gooey';
import { Img } from '#/components/images';
import { ButtonLink } from '#/components/links';

export type PageErrorType = 'not-found' | 'default';

export interface PageNotFoundProps extends NotFoundRouteProps, PageErrorBaseProps {
  type: 'not-found';
}

export interface PageErrorDefaultProps extends ErrorComponentProps, PageErrorBaseProps {
  type: 'default';
}

export type PageErrorProps = PageErrorDefaultProps | PageNotFoundProps;

export function PageError(props: PageErrorProps) {
  const message = React.useMemo(() => {
    if (props.message) {
      return props.message;
    }

    if (isNotFound(props)) {
      return 'The page you are looking for does not exist.';
    }

    return 'An error occurred while loading this page.';
  }, [props]);

  const imgSrc = React.useMemo(() => {
    if (isNotFound(props)) {
      return '/errors/not-found.png';
    }

    return '/errors/default.png';
  }, [props]);

  return (
    <div className={twMerge('w-full min-h-screen', props.className)}>
      <main className='pt-[10vh] pb-[10vh]'>
        <div className='flex flex-col items-center justify-center gap-8'>
          <div className='max-w-[600px] mx-auto'>
            <Img
              src={imgSrc}
              height={200}
              width={200}
              alt='Page not found'
              className='w-full h-auto'
              responsive
            />
          </div>

          <div className='text-center pt-8'>
            <p className='text-2xl font-semibold'>{message}</p>
          </div>
          <Stack direction='row' spacing={4} className='justify-center'>
            <Button onClick={() => window.history.back()} variant='contained'>
              Go Back
            </Button>
            <ButtonLink to='/' variant='outlined'>
              Go Home
            </ButtonLink>
          </Stack>
        </div>
      </main>
    </div>
  );
}

/*
|------------------
| Internals
|------------------
*/

interface PageErrorBaseProps {
  type: PageErrorType;
  className?: string;

  /**
   * Override the default error message.
   */
  message?: string;
}

function isNotFound(props: PageErrorProps): props is PageNotFoundProps {
  return props.type === 'not-found';
}
